import React from 'react'
import { Link } from 'react-router-dom'
import useAuth0ClientConfig from '../../lib/auth0/use-auth0-client-config'
import {
  makeLogin,
  LoginError,
  makeGoogleLogin,
  makeAppleLogin,
} from '../../lib/apiService'
import useLogger from '../../lib/logger/use-logger'
import useNotifications from '../../lib/notifications/use-notifications'
import useAnalytics from '../../lib/analytics/use-analytics'
import {
  trackForgotPassword,
  trackSignUpLink,
} from '../../lib/analytics/events'
import LoginForm, {
  OnChangeEmail,
  OnFailure,
  RenderForgotPasswordLink,
  RenderSignUpLink,
  RenderCreateNewPasswordLink,
} from '../../components/forms/login'
import OnboardingPage from '../onboarding-page'
import { SIGN_UP, CREATE_NEW_PASSWORD } from '../paths'
import { UserFlow } from '../../lib/apiService/realApi'

interface Props {
  setEmail(email: string): void
  draftEmail: string
  setDraftEmail(email: string): void
  forgotPasswordUrl: string
}

const LoginPage: React.FC<Props> = (props) => {
  const logger = useLogger()
  const { dataLayer } = useAnalytics()
  const { setNotification, clearNotifications } = useNotifications()
  const { cookieDomain } = useAuth0ClientConfig()

  const onChangeEmail: OnChangeEmail = (email) => {
    props.setDraftEmail(email)
  }

  const onFailure: OnFailure = (data) => {
    data.errors.forEach((err) => {
      switch (err) {
        case LoginError.EmailLacksPassword:
        // falls through
        case LoginError.UnusedEmail:
        // falls through
        case LoginError.InvalidCredentials:
          setNotification({
            id: 'login-page/on-failure-invalid-credentials',
            type: 'failure',
            message: 'Please enter a valid email or password and try again',
          })
          break
        case LoginError.PasswordLeaked:
          setNotification({
            id: 'login-page/on-failure-password-leaked',
            type: 'failure',
            message:
              'For security reasons this account has been blocked. Please contact Customer Support.',
          })
          break
        case LoginError.BlockedUser:
          setNotification({
            id: 'login-page/on-failure-blocked-user',
            type: 'failure',
            message: 'The user is blocked',
          })
          break
        case LoginError.TooManyAttempts:
          setNotification({
            id: 'login-page/on-failure-too-many-attempts',
            type: 'failure',
            message:
              'This account has been blocked after too many failed login attempts. Please contact Customer Support.',
          })
          break
        default:
          setNotification({
            id: 'login-page/on-failure',
            type: 'failure',
            message:
              'There was a problem loading this information. Please try again.',
          })
      }
    })
  }

  const renderForgotPasswordLink: RenderForgotPasswordLink = (className) => (
    <a
      href={props.forgotPasswordUrl}
      onClick={() => dataLayer(trackForgotPassword())}
      className={className}
    >
      Forgot password
    </a>
  )

  const renderSignUpLink: RenderSignUpLink = (className) => (
    <Link
      to={SIGN_UP}
      onClick={() => dataLayer(trackSignUpLink())}
      className={className}
    >
      Sign up
    </Link>
  )

  const renderCreateNewPasswordLink: RenderCreateNewPasswordLink = (
    className
  ) => (
    <Link to={CREATE_NEW_PASSWORD} className={className}>
      Create password
    </Link>
  )

  const login = makeLogin({ logger })

  return (
    <OnboardingPage>
      <LoginForm
        draftEmail={props.draftEmail}
        onChangeEmail={onChangeEmail}
        onFailure={onFailure}
        login={(...args) => {
          clearNotifications()
          return login(...args)
        }}
        googleLogin={makeGoogleLogin({
          logger,
          dataLayer,
          userFlow: UserFlow.LOGIN_GOOGLE,
          cookieDomain,
        })}
        appleLogin={makeAppleLogin({
          logger,
          dataLayer,
          userFlow: UserFlow.LOGIN_APPLE,
          cookieDomain,
        })}
        renderForgotPasswordLink={renderForgotPasswordLink}
        renderSignUpLink={renderSignUpLink}
        renderCreateNewPasswordLink={renderCreateNewPasswordLink}
      />
    </OnboardingPage>
  )
}

export default LoginPage
